import { useReducedMotion } from 'motion/react'

/**
 * Motion presets and the app's easing curve. General infrastructure: nothing
 * in here knows about any particular view.
 *
 * Reduced motion here means *less* motion, not none: the opacity fade stays
 * because it still communicates "this arrived", while every translation and
 * every chart draw-in is removed. Every hook reads the preference itself so
 * no caller has to remember to.
 */

/** Strong ease-out — instant movement, settles without overshoot. */
export const EASE_OUT = [0.23, 1, 0.32, 1] as const

/** Scroll reveal: fades up once, fades only under reduced motion. */
export function useReveal(delay = 0) {
  const reduce = !!useReducedMotion()
  return {
    initial: { opacity: 0, y: reduce ? 0 : 12 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-8% 0px' } as const,
    transition: { duration: reduce ? 0.2 : 0.5, delay: reduce ? 0 : delay, ease: EASE_OUT },
  }
}

/** Recharts draw-in, zeroed when the user asked for less motion. */
export function useChartMotion(duration: number) {
  const reduce = !!useReducedMotion()
  return { isAnimationActive: !reduce, animationDuration: reduce ? 0 : duration }
}

/** Bar/segment fill: `scaleX` from the left edge, never an animated `width`. */
export function useFillMotion(pct: number, delay = 0) {
  const reduce = !!useReducedMotion()
  const clamped = Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) / 100 : 0
  return {
    initial: { transform: `scaleX(${reduce ? clamped : 0})` },
    whileInView: { transform: `scaleX(${clamped})` },
    viewport: { once: true } as const,
    transition: { duration: reduce ? 0 : 0.7, delay: reduce ? 0 : delay, ease: EASE_OUT },
  }
}
